/**
 * Shared constants for the GiddyCarrot application.
 * 
 * These constants are used across multiple components to ensure consistency.
 * Import specific constants as needed to avoid loading unused values.
 */

// ============================================================================
// BRAND COLORS
// ============================================================================

/**
 * Brand colors used throughout the application.
 * Use these instead of hardcoding hex values.
 */
export const COLORS = {
  primary: '#52A675',      // Main green
  primaryHover: '#4A9168', // Darker green for hover states
  dark: '#2F5233',         // Dark green for text
  accent: '#C7F464',       // Lime accent
  orange: '#F4A261',       // Orange/warning
  blue: '#3CDEFA',         // Blue accent
  purple: '#9370DB',       // Purple
  red: '#FF6B6B',          // Red/danger
  teal: '#4ECDC4',         // Teal
  lightBlue: '#45B7D1',    // Light blue
  mint: '#96CEB4',         // Mint
  cream: '#FFEAA7',        // Cream
};

/**
 * Chart colors palette for data visualization.
 */
export const CHART_COLORS = [
  '#52A675', '#F4A261', '#C7F464', '#3CDEFA', '#9370DB',
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7'
];

// ============================================================================
// FREE TIER LIMITS
// ============================================================================

/**
 * Limits for free tier users.
 * These are also defined in SubscriptionContext but exported here for reference.
 */
export const FREE_LIMITS = {
  seeds: 100,
  plantings: 100,
  harvests: 100,
  tasks: 100,
  layouts: 1,
};

// ============================================================================
// TASK CATEGORIES
// ============================================================================

/**
 * Task category definitions with icons, colors, and emojis.
 * Used in Tasks.js and Dashboard.js.
 * 
 * Note: Icon components must be imported separately from lucide-react.
 * This array provides the category metadata without icon components.
 */
export const TASK_CATEGORY_IDS = {
  WATERING: 'watering',
  FERTILIZING: 'fertilizing',
  WEEDING: 'weeding',
  PRUNING: 'pruning',
  PEST_CONTROL: 'pest_control',
  HARVEST: 'harvest',
  OTHER: 'other',
};

/**
 * Task category metadata (without icon components for serialization).
 */
export const TASK_CATEGORIES = [
  { id: 'watering', label: 'Water', color: '#3CDEFA', emoji: '💧' },
  { id: 'fertilizing', label: 'Fertilize', color: '#9370DB', emoji: '🧪' },
  { id: 'weeding', label: 'Weed', color: '#52A675', emoji: '🌿' },
  { id: 'pruning', label: 'Prune', color: '#8d6f24', emoji: '✂️' },
  { id: 'pest_control', label: 'Pest Control', color: '#FF6B6B', emoji: '🐛' },
  { id: 'harvest', label: 'Harvest', color: '#FFA500', emoji: '🥕' },
  { id: 'other', label: 'Other', color: '#689caf', emoji: '📋' },
];

/**
 * Get category metadata by ID.
 * @param {string} categoryId - The category ID
 * @returns {Object|undefined} - The category metadata or undefined
 */
export const getTaskCategory = (categoryId) => {
  return TASK_CATEGORIES.find(cat => cat.id === categoryId);
};

// ============================================================================
// RECURRENCE PATTERNS
// ============================================================================

/**
 * Recurrence options for tasks.
 */
export const RECURRENCE_OPTIONS = [
  { id: 'none', label: 'One-time' },
  { id: 'daily', label: 'Daily' },
  { id: 'every_x_days', label: 'Every X days' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'monthly', label: 'Monthly' },
];

// ============================================================================
// PLANTING STATUSES
// ============================================================================

/**
 * Planting status progression.
 */
export const PLANTING_STATUSES = [
  { id: 'planned', label: 'Planned', color: '#9370DB' },
  { id: 'started', label: 'Started', color: '#3CDEFA' },
  { id: 'transplanted', label: 'Transplanted', color: '#F4A261' },
  { id: 'completed', label: 'Completed', color: '#52A675' },
];

/**
 * Get planting status metadata by ID.
 * @param {string} statusId - The status ID
 * @returns {Object|undefined} - The status metadata or undefined
 */
export const getPlantingStatus = (statusId) => {
  return PLANTING_STATUSES.find(s => s.id === statusId);
};

// ============================================================================
// WEIGHT UNITS
// ============================================================================

/**
 * Weight units for harvests.
 */
export const WEIGHT_UNITS = [
  { id: 'lb', label: 'lb', labelFull: 'Pounds' },
  { id: 'oz', label: 'oz', labelFull: 'Ounces' },
  { id: 'kg', label: 'kg', labelFull: 'Kilograms' },
  { id: 'g', label: 'g', labelFull: 'Grams' },
];

/**
 * Convert weight to ounces for totaling.
 * @param {number} weight - The weight value
 * @param {string} unit - The unit (lb, oz, kg, g)
 * @returns {number} - Weight in ounces
 */
export const convertToOunces = (weight, unit) => {
  if (!weight || typeof weight !== 'number' || Number.isNaN(weight)) return 0;
  switch (unit) {
    case 'lb': return weight * 16;
    case 'oz': return weight;
    case 'kg': return weight * 35.274;
    case 'g': return weight * 0.035274;
    default: return weight;
  }
};

// ============================================================================
// GARDEN LAYOUT ZOOM
// ============================================================================

/**
 * Base cell size in pixels for the garden layout grid.
 * All zoom levels are relative to this base value.
 */
export const BASE_CELL_SIZE = 100;

/**
 * Maximum number of plants allowed per grid cell (1 primary + up to 2 secondaries).
 */
export const MAX_PLANTS_PER_CELL = 3;

/**
 * Normalizes a stored cell plant value to always return an array.
 * Handles backwards compatibility: existing single-object entries are wrapped
 * in an array with isPrimary: true, without mutating stored data.
 */
export const normalizeCellPlants = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [{ ...value, isPrimary: true }];
};

/**
 * Discrete zoom level steps (percentage values).
 * Cell size = BASE_CELL_SIZE × (zoomLevel / 100).
 * Range: 25% (mobile overview) → 200% (detail work).
 */
export const ZOOM_STEPS = [25, 50, 75, 100, 125, 150, 200];

// ============================================================================
// GARDEN LAYOUT ELEMENTS
// ============================================================================

/**
 * Garden layout element types.
 */
export const LAYOUT_ELEMENT_TYPES = [
  { id: 'bed', label: 'Raised Bed', color: '#8B5A2B', emoji: '🌱' },
  { id: 'path', label: 'Path', color: '#A0A0A0', emoji: '🚶' },
  { id: 'grass', label: 'Grass/Lawn', color: '#7CCD7C', emoji: '🌿' },
  { id: 'structure', label: 'Structure', color: '#4A4A4A', emoji: '🏠' },
  { id: 'water', label: 'Water Feature', color: '#4A90D9', emoji: '💧' },
  { id: 'compost', label: 'Compost', color: '#6B4423', emoji: '🍂' },
];

// ============================================================================
// SEED FIELDS
// ============================================================================

/**
 * Maximum character count for the seed spacing text field.
 */
export const SPACING_MAX_CHARS = 20;

// ============================================================================
// PLANNING PANEL
// ============================================================================

/**
 * Planning Panel dimensions and settings.
 * The Planning Panel mirrors the Manage panel on the opposite side.
 */
export const PLANNING_PANEL = {
  /** Desktop panel width (Tailwind class) — mirrors Manage panel */
  DESKTOP_WIDTH: 'w-80',
  /** Mobile drawer max-height */
  MOBILE_MAX_HEIGHT: '70vh',
  /** Minimum grid width (px) before auto-collapsing the Planning Panel */
  MIN_GRID_WIDTH: 400,
  /** localStorage key prefix for panel open/closed state (append layout ID) */
  STORAGE_KEY_PREFIX: 'giddy_layout_plan_panel_',
  /** Grid highlight animation duration in ms */
  HIGHLIGHT_DURATION: 10000,
  /** Mobile button z-index */
  MOBILE_BUTTON_Z: 'z-30',
  /** Mobile drawer z-index */
  MOBILE_DRAWER_Z: 'z-50',
  /** Mobile backdrop z-index */
  MOBILE_BACKDROP_Z: 'z-40',
  /** Bottom offset for mobile buttons (accounts for scrollbar) */
  MOBILE_BUTTON_BOTTOM: 'bottom-4',
  /** Slide animation duration class */
  ANIMATION_DURATION: 'duration-300',
};

// ============================================================================
// PLANT / SEED CONSTANTS
// ============================================================================

/**
 * Growing season options for annual seeds.
 * Matches the `season` column in both the `seeds` and `catalog_plants` tables.
 */
export const SEED_SEASONS = [
  { id: 'warm', label: 'Warm Season' },
  { id: 'cool', label: 'Cool Season' },
];

/**
 * Harvest season options.
 * Matches the `harvest_season` text[] values in both the `seeds` and
 * `catalog_plants` tables. Always lowercase.
 * These are the canonical options — `SeasonPicker` and all filter logic
 * should derive from this array rather than defining their own local copies.
 */
export const HARVEST_SEASON_OPTIONS = [
  { id: 'spring', label: 'Spring' },
  { id: 'summer', label: 'Summer' },
  { id: 'fall', label: 'Fall' },
];

/**
 * Preferred start method options for annual seeds.
 * Matches `preferred_start_method` values in the `seeds` table and the
 * `preferred_start_method` column in `catalog_plants`.
 */
export const START_METHODS = [
  { id: 'indoor', label: 'Start Indoors' },
  { id: 'direct_sow', label: 'Direct Sow' },
];

/**
 * Plant categories shared across seeds and catalog_plants.
 * 'nut' exists in seeds but not in the catalog (v1).
 */
export const PLANT_CATEGORIES = [
  { id: 'vegetable', label: 'Vegetable' },
  { id: 'herb', label: 'Herb' },
  { id: 'flower', label: 'Flower' },
  { id: 'fruit', label: 'Fruit' },
  { id: 'fungi', label: 'Fungi' },
  { id: 'nut', label: 'Nut' },
];

/**
 * Plant types for the seeds table.
 */
export const PLANT_TYPES = [
  { id: 'seed', label: 'Seed' },
  { id: 'perennial', label: 'Perennial' },
  { id: 'tree', label: 'Tree' },
  { id: 'bush', label: 'Bush' },
];

// ============================================================================
// PLANT CATALOG BROWSE
// ============================================================================

/**
 * Chip order for Browse Catalog tab.
 * Each id maps to a catalog_plants.category value.
 * 'all' is handled with a catch-all query.
 */
export const CATALOG_CHIPS = [
  { id: 'vegetables', label: 'Vegetables' },
  { id: 'herbs', label: 'Herbs' },
  { id: 'flowers', label: 'Flowers' },
  { id: 'fruit', label: 'Fruit' },
  { id: 'fungi', label: 'Fungi' },
  { id: 'all', label: 'All' },
];

/**
 * Display order for vegetable plant groups in the accordion.
 * Any record whose first plant_group value isn't listed here is placed in 'specialty'.
 */
export const PLANT_GROUP_ORDER = [
  'fruiting',
  'cucumbers',
  'leafy_greens',
  'legumes',
  'summer_squash',
  'brassicas',
  'roots_bulbs',
  'winter_squash',
  'melons',
  'grains',
  'specialty',
];

/** Display order for flower plant groups in the accordion. */
export const FLOWER_GROUP_ORDER = [
  'cut_flower',
  'pollinator',
  'edible',
];

/** Human-readable labels for plant_group values. */
export const PLANT_GROUP_LABELS = {
  fruiting: 'Fruiting Vegetables',
  cucumbers: 'Cucumbers',
  leafy_greens: 'Leafy Greens',
  legumes: 'Legumes',
  summer_squash: 'Summer Squash & Zucchini',
  brassicas: 'Brassicas',
  roots_bulbs: 'Roots & Bulbs',
  winter_squash: 'Winter Squash & Pumpkins',
  melons: 'Melons & Watermelons',
  grains: 'Grains & Corn',
  specialty: 'Specialty',
  herbs: 'Herbs',
  pollinator: 'Pollinators & Beneficials',
  cut_flower: 'Cut Flowers',
  edible: 'Edible Flowers',
  fruit: 'Fruit',
  fungi: 'Fungi',
};

/** Human-readable labels for growth_habit values. */
export const GROWTH_HABIT_LABELS = {
  determinate: 'Determinate',
  indeterminate: 'Indeterminate',
  'semi-determinate': 'Semi-det.',
  bush: 'Bush',
  vining: 'Vining',
  climbing: 'Climbing/Pole',
};

// ============================================================================
// FROST DATE AUTO-DETECTION
// ============================================================================

/**
 * Maximum distance (km) from a user's geocoded location to the nearest NOAA station.
 * If no station falls within this radius, the feature falls back to manual entry.
 * 100 miles was chosen as a reasonable US station-density threshold.
 */
export const NOAA_MAX_STATION_DISTANCE_KM = 160.9344; // 100 miles

/**
 * Latitude of the Tropic of Cancer (°N). US locations below this threshold with no NOAA
 * frost station nearby (Hawaii, Puerto Rico, Guam, USVI, etc.) are treated as frost-free —
 * sea-level tropical stations never freeze, so NOAA publishes no freeze probability data for them.
 */
export const TROPIC_OF_CANCER_LAT = 23.5;

// ============================================================================
// DATE FILTER PRESETS
// ============================================================================

/**
 * Date filter presets for data views.
 */
export const DATE_FILTER_PRESETS = [
  { id: 'this_year', label: 'This Year' },
  { id: 'this_month', label: 'This Month' },
  { id: 'last_month', label: 'Last Month' },
  { id: 'last_30_days', label: 'Last 30 Days' },
  { id: 'all_time', label: 'All Time' },
  { id: 'custom', label: 'Custom Range...' },
];
